import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/catalyst-ui/table'
import { Badge } from '@/components/catalyst-ui/badge'
import ConnectProjectDropdown from './ConnectProjectDropdown'
import { Dialog } from '@/components/catalyst-ui/dialog'
import { Button } from '@/components/catalyst-ui/button'
import { Heading } from '@/components/catalyst-ui/heading'
import DomainActionMenu from './DomainActionMenu'
import greenTick from '../../../assets/icons/20/greenTick.svg';
import refresh from '../../../assets/icons/20/refresh.svg';
import { useProjects } from '@/features/workspace/hooks/useProjects'
import { useActiveWorkspace, useSelectionStore } from '@/state/selection.store'
import { hasFeature } from '@/lib/hasFeature'
import { useCheckDomainWithNsStatus } from '../hooks/useMyDomains'
import { CheckBadgeIcon } from '@heroicons/react/24/solid'
import { XMarkIcon } from '@heroicons/react/24/solid'
import { useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { usePermissions } from '@/hooks/usePermissions'
import ConnectProjectConfirmDialog from './ConnectProjectConfirmDialog'
import DomainConnectedSuccessDialog from './DomainConnectedSuccessDialog'
import ErrorDialog from './ErrorDialog'

export default function DomainTable({ domains = [], onManage, onConnect, onDisconnect, onDelete, onValidate, onDomainClick, onRecordsClick }) {
  const { workspaceId } = useActiveWorkspace()
  const { projects } = useProjects(workspaceId)
  const manualCheckMutation = useCheckDomainWithNsStatus(true) // Show toast for manual checks
  const navigate = useNavigate()
  const [checkingDomainId, setCheckingDomainId] = useState(null)
  const { hasRole } = usePermissions()
  const isViewer = hasRole('viewer')
  // Helper function to get project by comparing domain.project_id with project._id
  const getProjectById = (projectId) => {
    if (!projectId) return null

    // Convert both to strings for comparison
    const projectIdStr = String(projectId)

    // Try to find project by comparing _id field
    const foundProject = projects.find(project => {
      if (project._id) {
        return String(project._id) === projectIdStr
      }
      if (project.id) {
        return String(project.id) === projectIdStr
      }
      return false
    })

    console.log('Project lookup:', {
      lookingFor: projectIdStr,
      availableProjects: projects.map(p => ({ _id: p._id, name: p.name })),
      found: foundProject ? foundProject.name : 'NOT FOUND'
    })

    return foundProject
  }

  // Helper function to check if domain is connected
  const isDomainConnected = (domain) => {
    // For withNS domains, check if project_id exists
    if (domain.domainType === 'withNS') {
      return !!domain.project_id
    }
    // For third-party domains, check project field
    if (domain.domainType === 'thirdParty') {
      return !!domain.project
    }
    // Fallback to connected field
    return !!domain.connected
  }

  // Helper function to get connected project ID
  const getConnectedProjectId = (domain) => {
    // For withNS domains, use project_id
    if (domain.domainType === 'withNS') {
      return domain.project_id
    }
    // For third-party domains, use project field
    if (domain.domainType === 'thirdParty') {
      return domain.project
    }
    return null
  }

  // State for popups
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false)
  const [showConnectConfirmDialog, setShowConnectConfirmDialog] = useState(false)
  const [showSuccessDialog, setShowSuccessDialog] = useState(false)
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [selectedProject, setSelectedProject] = useState(null)
  const [connectedProject, setConnectedProject] = useState(null)
  const [showErrorDialog, setShowErrorDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  // Helper function to check if a project is already connected to any domain
  const isProjectConnectedToAnyDomain = (projectId) => {
    return domains.some(domain => {
      const connectedProjectId = getConnectedProjectId(domain);
      return connectedProjectId && String(connectedProjectId) === String(projectId) && isDomainConnected(domain);
    });
  }

  const handleProjectSelect = (domain, project) => {
    // Show confirmation dialog instead of connecting directly
    setSelectedDomain(domain)
    setSelectedProject(project)
    setShowConnectConfirmDialog(true)
  }

  const handleConfirmConnect = async () => {
    if (onConnect && selectedDomain && selectedProject) {
      try {
        await onConnect(selectedDomain, selectedProject)
        // Show success dialog after successful connection
        setConnectedProject(selectedProject)
        setShowConnectConfirmDialog(false)
        setShowSuccessDialog(true)
      } catch (error) {
        // Error handling is done in the parent component
        console.error('Failed to connect project:', error)
        setShowConnectConfirmDialog(false)
      }
    }
  }

  const handleDisconnectClick = (domain) => {
    setSelectedDomain(domain)
    setShowDisconnectDialog(true)
  }

  const handleConfirmDisconnect = async () => {
    setShowDisconnectDialog(false)

    try {
      if (onDisconnect) {
        await onDisconnect(selectedDomain)
      }
    } catch (error) {
      console.error('Failed to disconnect domain:', error);
    }
  }

  const handleManualDNSCheck = async (domain) => {
    const domainId = domain.id || domain._id || domain.name
    setCheckingDomainId(domainId)
    try {
      if (onValidate) {
        // Use the custom validation function passed from parent
        await onValidate(domain)
      } else {
        // Fallback to default withNS validation
        await manualCheckMutation.mutateAsync({
          workspaceId,
          domain: domain.name
        })
      }
    } catch (error) {
      console.error('DNS check failed:', error)
    } finally {
      setCheckingDomainId(null)
    }
  }
  return (
    <div className="space-y-4">
      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <Table className="w-full">
          <TableHead>
            <TableRow>
              <TableHeader className="text-left">Domain Name</TableHeader>
              <TableHeader className="text-left">Site</TableHeader>
              <TableHeader className="text-left">Renewal</TableHeader>
              <TableHeader className="text-left">Expires</TableHeader>
              <TableHeader className="text-left">Records</TableHeader>
              <TableHeader className="text-left">Status</TableHeader>
              <TableHeader className="w-12"></TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {domains.map((domain, index) => (
              <TableRow key={index} className='hover:bg-[#FAFAFA] transition-colors'>
                {/* Domain name */}
                <TableCell className="!font-normal text-sm">
                  <button
                    onClick={() => {
                      if (domain.domainType === 'thirdParty') {
                        // If both DNS and TXT verified, go to config page; otherwise go to records page
                        if (domain.dnsVerified && domain.txtVerified) {
                          navigate(`/domains/my-domains/config/${domain.name}?workspace=${workspaceId || ''}`)
                        } else {
                          navigate(`/domains/my-domain/records?domain=${domain.name}&workspace=${workspaceId || ''}`)
                        }
                      } else {
                        navigate(`/domains/my-domains/${domain.name}?workspace=${workspaceId || ''}`)
                      }
                    }}
                    className="text-black hover:underline cursor-pointer"
                  >
                    {domain.name}
                  </button>
                </TableCell>

                {/* Site */}
                <TableCell>
                  {isDomainConnected(domain) ? (
                    <div className="flex items-center gap-2">
                      <Badge color="lime" className="!text-sm !font-medium">Connected</Badge>
                      <div className="w-px h-6 bg-[#E4E4E7]" />
                      <span
                        title={getProjectById(getConnectedProjectId(domain))?.name || ''}
                        className="text-sm text-black font-medium truncate max-w-[170px]"
                      >
                        {getProjectById(getConnectedProjectId(domain))?.name || ''}
                      </span>
                    </div>
                  ) : isViewer ? (
                    <span className="text-sm text-[#09090B80] font-medium">Not connected</span>
                  ) : (
                    <ConnectProjectDropdown
                      projects={projects.filter(project => !isProjectConnectedToAnyDomain(project.id || project._id))}
                      onSelect={(project) => {
                        if (project?.order?.paid) {
                          handleProjectSelect(domain, project)
                        } else {
                          const normalizedProject = { ...project, id: project.id || project._id }
                          useSelectionStore.getState().setCurrentProject(normalizedProject)
                          hasFeature('DOMAIN_CONNECTION', () => {
                            handleProjectSelect(domain, project)
                          }, ['limitPremium'])
                        }
                      }}
                      buttonText="Connect project"
                      buttonClassName="!text-[#09090B80] underline font-medium cursor-pointer hover:text-gray-700 p-2 hover:bg-[#F4F4F5] rounded-lg -ml-1"
                      skipGating={true}
                    />
                  )}
                </TableCell>

                {/* Renewal */}
                <TableCell>
                  {domain.renewal === 'Auto Renewal' ? (
                    <span className="text-[#09090BCC] text-sm">Auto Renewal</span>
                  ) : domain.renewal === 'Third party' || domain.domainType === 'thirdParty' ? (
                    <span className="text-gray-500 text-sm">Third-party</span>
                  ) : (
                    <span className="text-gray-500 text-sm">Disabled</span>
                  )}
                </TableCell>

                {/* Expires */}
                <TableCell className="text-sm text-gray-600">
                  {domain.expiresAt || domain.expires || '-'}
                </TableCell>

                {/* Records */}
                <TableCell>
                  {domain.domainType === 'thirdParty' ? (
                    // Third-party domains show validation status
                    domain.dnsVerified && domain.txtVerified ? (
                      <span className="text-[#15803D] text-sm font-medium">Validated</span>
                    ) : (
                      <button
                        className="text-[#F59E0B] underline cursor-pointer text-sm font-medium hover:text-amber-700"
                        onClick={() => navigate(`/domains/my-domain/records?domain=${domain.name}&workspace=${workspaceId || ''}`)}
                      >
                        Complete Validation
                      </button>
                    )
                  ) : domain.expires && domain.expires !== '-' ? (
                    <div className="flex gap-2">
                      <button className="text-black underline cursor-pointer text-sm data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded" onClick={() => onRecordsClick && onRecordsClick(domain)}>DNS</button>
                      <div className="w-px h-6 bg-[#E4E4E7]" />
                      <button className="text-black underline cursor-pointer text-sm data-focus:!ring-2 data-focus:!ring-indigo-500 data-focus:!ring-offset-2 rounded" onClick={() => onRecordsClick && onRecordsClick(domain)}>NS</button>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-sm">-</span>
                  )}
                </TableCell>

                {/* Status */}
                <TableCell>
                  <div className="flex items-center justify-center -ml-5">
                    {domain.dnsVerified ? (
                      <img src={greenTick} alt="Verified" />
                    ) : (
                      <img
                        src={refresh}
                        alt="Check DNS"
                        className={`cursor-pointer hover:opacity-70 transition-transform ${checkingDomainId === (domain.id || domain._id || domain.name) ? 'animate-spin' : ''
                          }`}
                        onClick={() => handleManualDNSCheck(domain)}
                      />
                    )}
                  </div>
                </TableCell>

                {/* Action menu */}
                <TableCell>
                  {!isViewer && (
                    <DomainActionMenu
                      domain={domain}
                      onManage={onManage}
                      onConnect={onConnect}
                      onDisconnect={onDisconnect}
                      onDelete={onDelete}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Cards */}
      <div className="block md:hidden space-y-4">
        {domains.map((domain) => (
          <div
            key={domain.id}
            className="bg-white border border-gray-200 rounded-lg p-4 space-y-"
          >
            <div className="flex justify-between items-start">
              <div>
                <button
                  onClick={() => {
                    if (domain.domainType === 'thirdParty') {
                      navigate(`/domains/my-domains/config/${domain.name}?workspace=${workspaceId || ''}`)
                    } else {
                      navigate(`/domains/my-domains/${domain.name}?workspace=${workspaceId || ''}`)
                    }
                  }}
                  className="text-black hover:underline cursor-pointer"
                >
                  {domain.name}
                </button>
                {(domain.expiresAt || domain.expires !== '-') && (<p className="text-sm text-gray-600">
                  Expires: {domain.expiresAt || domain.expires || '-'}
                </p>
                )}
              </div>
              {!isViewer && (
                <DomainActionMenu
                  domain={domain}
                  onManage={onManage}
                  onConnect={onConnect}
                  onDisconnect={onDisconnect}
                  onDelete={onDelete}
                />
              )}
            </div>

            <div className="flex items-center justify-between">
              <div>
                {isDomainConnected(domain) ? (
                  <div className="flex items-center gap-2">
                    <Badge color="lime">Connected</Badge>
                    <div className="w-px h-6 bg-[#E4E4E7]" />
                    <span className="text-sm font-medium truncate max-w-[100px]">
                      {getProjectById(getConnectedProjectId(domain))?.name}
                    </span>
                  </div>
                ) : isViewer ? (
                  <span className="text-sm text-[#09090B80] font-medium">Not connected</span>
                ) : (
                  <ConnectProjectDropdown
                    projects={projects.filter(project => !isProjectConnectedToAnyDomain(project.id || project._id))}
                    onSelect={(project) => {
                      if (project?.order?.paid) {
                        handleProjectSelect(domain, project)
                      } else {
                        const normalizedProject = { ...project, id: project.id || project._id }
                        useSelectionStore.getState().setCurrentProject(normalizedProject)
                        hasFeature('DOMAIN_CONNECTION', () => {
                          handleProjectSelect(domain, project)
                        }, ['limitPremium'])
                      }
                    }}
                    buttonText="Connect project"
                    buttonClassName="text-black underline text-sm cursor-pointer hover:text-gray-700"
                    skipGating={true}
                  />
                )}
              </div>

              <div className="scale-80 mr-2.5">
                {domain.dnsVerified ? (
                  <img src={greenTick} alt="Verified" />
                ) : (
                  <img
                    src={refresh}
                    alt="Check DNS"
                    className={`cursor-pointer hover:opacity-70 transition-transform ${checkingDomainId === (domain.id || domain._id || domain.name) ? 'animate-spin' : ''
                      }`}
                    onClick={() => handleManualDNSCheck(domain)}
                  />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Connect Confirmation Dialog */}
      <ConnectProjectConfirmDialog
        open={showConnectConfirmDialog}
        onClose={() => {
          setShowConnectConfirmDialog(false)
          setSelectedDomain(null)
          setSelectedProject(null)
        }}
        title="Are you sure you want to connect domain?"
        description={
          <>
            You are going to connect{' '}
            <strong className="text-black font-bold">{selectedDomain?.name}</strong>{' '}
            into the project{' '}
            <strong className="text-black font-bold">
              '{selectedProject?.name || 'project name'}'
            </strong>
            , you can publish to domain later
          </>
        }
        confirmLabel="Connect now"
        cancelLabel="Cancel"
        onConfirm={handleConfirmConnect}
      />

      {/* Success Dialog */}
      <DomainConnectedSuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        heading={
          `${selectedDomain?.name || ''} connected to ${connectedProject?.name || 'project'} successfully`
        }
        cancelLabel="Cancel"
        primaryLabel={selectedDomain?.domainType === 'thirdParty' ? 'Verify DNS Records' : 'Go to project'}
        onPrimary={() => {
          if (selectedDomain?.domainType === 'thirdParty') {
            // Navigate to records page for DNS re-verification
            navigate(`/domains/my-domain/records?domain=${encodeURIComponent(selectedDomain.name)}&workspace=${workspaceId || ''}`)
          } else if (connectedProject) {
            const projectId = connectedProject.id || connectedProject._id || connectedProject.uniqueUrl
            if (projectId && workspaceId) {
              navigate(`/overview?workspace=${workspaceId}&project=${projectId}`)
            }
          }
          setShowSuccessDialog(false)
        }}
      />

      {/* Error Dialog */}
      <ErrorDialog
        open={showErrorDialog}
        onClose={() => setShowErrorDialog(false)}
        title="Failed to connect"
        message={errorMessage || 'Failed to connect domain to project.'}
        closeLabel="Close"
      />
    </div>
  )
}
